import { apiFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || '';
export const OPEN_TIA_AGENT_SETTINGS_EVENT = 'realplc:open-tia-agent-settings';

export function requestOpenTiaAgentSettings(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_TIA_AGENT_SETTINGS_EVENT));
  }
}

export interface TiaAgentReleaseInfo {
  available: boolean;
  source: 'cloud' | 'github' | 'github-direct' | 'configured' | 'unavailable';
  repository: string | null;
  assetName: string;
  tagName: string | null;
  version: string | null;
  minimumSupportedVersion: string | null;
  downloadUrl: string | null;
  releasePageUrl: string | null;
  releaseNotes: string | null;
  publishedAt: string | null;
  sha256: string | null;
  sizeBytes: number | null;
}

export type TiaAgentUpdateState =
  | 'install'
  | 'current'
  | 'available'
  | 'required'
  | 'unknown';

interface ParsedVersion {
  parts: number[];
  prerelease: string[] | null;
}

function parseVersion(value: string | null | undefined): ParsedVersion | null {
  const match = value?.match(/(\d+(?:\.\d+)+)(?:-([0-9A-Za-z.-]+))?/);
  if (!match) return null;
  const parts = match[1].split('.').map((part) => Number.parseInt(part, 10));
  if (!parts.every(Number.isFinite)) return null;
  return {
    parts,
    prerelease: match[2] ? match[2].split('.') : null,
  };
}

export function compareTiaAgentVersions(
  left: string | null | undefined,
  right: string | null | undefined,
): number | null {
  const leftParts = parseVersion(left);
  const rightParts = parseVersion(right);
  if (!leftParts || !rightParts) return null;

  const length = Math.max(leftParts.parts.length, rightParts.parts.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftParts.parts[index] ?? 0) - (rightParts.parts[index] ?? 0);
    if (difference !== 0) return difference < 0 ? -1 : 1;
  }

  if (!leftParts.prerelease && !rightParts.prerelease) return 0;
  if (!leftParts.prerelease) return 1;
  if (!rightParts.prerelease) return -1;

  const prereleaseLength = Math.max(
    leftParts.prerelease.length,
    rightParts.prerelease.length,
  );
  for (let index = 0; index < prereleaseLength; index += 1) {
    const leftIdentifier = leftParts.prerelease[index];
    const rightIdentifier = rightParts.prerelease[index];
    if (leftIdentifier === undefined) return -1;
    if (rightIdentifier === undefined) return 1;
    if (leftIdentifier === rightIdentifier) continue;

    const leftNumeric = /^\d+$/.test(leftIdentifier);
    const rightNumeric = /^\d+$/.test(rightIdentifier);
    if (leftNumeric && rightNumeric) {
      return Number.parseInt(leftIdentifier, 10) < Number.parseInt(rightIdentifier, 10) ? -1 : 1;
    }
    if (leftNumeric !== rightNumeric) return leftNumeric ? -1 : 1;
    return leftIdentifier < rightIdentifier ? -1 : 1;
  }
  return 0;
}

export function resolveTiaAgentUpdateState(
  currentVersion: string | null | undefined,
  release: TiaAgentReleaseInfo | null,
): TiaAgentUpdateState {
  if (!release?.available) return 'unknown';
  if (!currentVersion) return 'install';

  if (release.minimumSupportedVersion) {
    const minimumComparison = compareTiaAgentVersions(
      currentVersion,
      release.minimumSupportedVersion,
    );
    if (minimumComparison !== null && minimumComparison < 0) return 'required';
  }

  const latestComparison = compareTiaAgentVersions(currentVersion, release.version);
  if (latestComparison === null) return 'unknown';
  return latestComparison < 0 ? 'available' : 'current';
}

export function resolveTiaAgentDownloadUrl(release: TiaAgentReleaseInfo | null): string | null {
  const value = release?.downloadUrl;
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (!API_BASE) return value;
  return `${API_BASE.replace(/\/$/, '')}/${value.replace(/^\//, '')}`;
}

export async function loadTiaAgentRelease(): Promise<TiaAgentReleaseInfo> {
  const response = await apiFetch(`${API_BASE}/api/realplc-agent/releases/latest`);
  if (!response.ok) {
    let message = 'Failed to load RealPLC Agent release information';
    try {
      const body = await response.json() as { message?: unknown };
      if (typeof body.message === 'string' && body.message.trim()) message = body.message;
    } catch {
      // Keep the stable fallback message when a proxy returns a non-JSON error.
    }
    throw new Error(message);
  }
  return response.json() as Promise<TiaAgentReleaseInfo>;
}
